import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  FiGlobe,
  FiMapPin,
  FiUsers,
  FiCalendar,
  FiPlus,
  FiTrash2,
  FiCheck,
  FiArrowRight,
  FiArrowLeft,
  FiUploadCloud,
  FiImage
} from 'react-icons/fi';
import EventService from '../services/EventService';
import instance from '../services/AxiosServices';
import SearchableSelect from '../components/SearchableSelect';

const INDIAN_STATES_CITIES = {
  'Rajasthan': ['Bikaner', 'Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bhilwara', 'Alwar', 'Sikar', 'Churu', 'Hanumangarh', 'Sri Ganganagar'],
  'Punjab': ['Amritsar', 'Chandigarh', 'Ludhiana', 'Jalandhar', 'Patiala', 'Bathinda', 'Pathankot', 'Mohali', 'Hoshiarpur', 'Firozpur'],
  'Delhi NCR': ['New Delhi', 'Noida', 'Gurugram', 'Ghaziabad', 'Faridabad'],
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur', 'Amravati'],
  'Karnataka': ['Bengaluru', 'Mysuru', 'Hubballi', 'Mangaluru', 'Belagavi', 'Davangere', 'Ballari'],
  'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar', 'Junagadh'],
  'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Kharagpur'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Varanasi', 'Agra', 'Meerut', 'Prayagraj', 'Noida', 'Ghaziabad', 'Bareilly', 'Aligarh'],
  'Madhya Pradesh': ['Indore', 'Bhopal', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Satna'],
  'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Purnia', 'Darbhanga'],
  'Haryana': ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Karnal', 'Hisar', 'Rohtak'],
  'Himachal Pradesh': ['Shimla', 'Manali', 'Dharamshala', 'Solan', 'Mandi'],
  'Uttarakhand': ['Dehradun', 'Haridwar', 'Rishikesh', 'Haldwani', 'Roorkee'],
  'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa'],
  'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kollam']
};

const CreateEvent = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [detectingLoc, setDetectingLoc] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  const [categories, setCategories] = useState([]);
  const [dbStatesMap, setDbStatesMap] = useState(INDIAN_STATES_CITIES);
  const [step, setStep] = useState(1);

  // Core Form State
  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    about: '',
    eventType: 'IRL',
    category: '',
    posterUrl: '',
    bannerUrl: '',
    gallery: [],

    startDate: '',
    endDate: '',
    eventSchedules: [],
    duration: '3 Hours',
    ageLimit: '5yrs +',
    language: 'Hindi',
    startTime: '10:00 AM',
    endTime: '06:00 PM',
    registrationClosingDate: '',
    timezone: 'Asia/Kolkata (IST)',
    sendReminders: true,

    venueName: '',
    venueAddress: '',
    areaLocality: '',
    city: '',
    state: '',
    pinCode: '',
    country: 'India',
    googleMapsUrl: '',
    lat: '',
    lng: '',

    maxCapacity: 200,
    registrationLimitPerUser: 1,
    waitingListEnabled: true,
    availableSeats: 200,

    ticketTypes: [
      { id: 1, name: 'General Admission', price: 499, quantity: 150, salesStart: '', salesEnd: '' },
      { id: 2, name: 'VIP Pass', price: 999, quantity: 50, salesStart: '', salesEnd: '' },
    ],

    attachedContests: [],
  });

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [catsRes, citiesRes] = await Promise.all([
          EventService.getCategories().catch(() => null),
          instance.get('/cities').catch(() => null)
        ]);

        // Process Categories
        const rawCats = catsRes?.data || catsRes;
        const catList = Array.isArray(rawCats) ? rawCats : rawCats?.categories || [];
        if (catList.length > 0) {
          setCategories(catList);
        } else {
          setCategories([
            { _id: 'cat1', name: 'Cultural & Food' },
            { _id: 'cat2', name: 'Music & Concerts' },
            { _id: 'cat3', name: 'Tech & Hackathons' },
            { _id: 'cat4', name: 'Sports & Fitness' },
            { _id: 'cat5', name: 'Business & Networking' },
          ]);
        }

        // Process States and Cities from DB
        const rawCities = citiesRes?.data || citiesRes;
        if (rawCities?.statesMap && Object.keys(rawCities.statesMap).length > 0) {
          setDbStatesMap(prev => ({ ...INDIAN_STATES_CITIES, ...rawCities.statesMap }));
        } else if (Array.isArray(rawCities?.allCities)) {
          const map = {};
          rawCities.allCities.forEach(c => {
            const st = c.stateName || 'Other';
            if (!map[st]) map[st] = [];
            if (!map[st].includes(c.name)) map[st].push(c.name);
          });
          if (Object.keys(map).length > 0) {
            setDbStatesMap(prev => ({ ...INDIAN_STATES_CITIES, ...map }));
          }
        }
      } catch (err) {
        console.error('Error fetching master data:', err);
      }
    };
    fetchMasterData();
  }, []);

  const getCategoryName = (c) => {
    if (!c) return '';
    if (typeof c === 'string') return c;
    if (typeof c.name === 'string') return c.name;
    if (c.name && typeof c.name === 'object') return c.name.en || c.name.name || c.parentName || 'Category';
    return c.parentName || 'Category';
  };

  const generateDatesBetween = (startStr, endStr) => {
    if (!startStr) return [];
    const sDate = new Date(startStr);
    const eDate = endStr ? new Date(endStr) : sDate;
    if (isNaN(sDate.getTime()) || isNaN(eDate.getTime()) || eDate < sDate) {
      return [startStr];
    }
    const list = [];
    let curr = new Date(sDate);
    while (curr <= eDate) {
      list.push(curr.toISOString().split('T')[0]);
      curr.setDate(curr.getDate() + 1);
    }
    return list;
  };

  const recalculateDateSchedules = (startStr, endStr, existingSchedules = []) => {
    const dates = generateDatesBetween(startStr, endStr);
    const existingMap = {};
    (existingSchedules || []).forEach(s => {
      if (s.date) existingMap[s.date] = s.slots || [];
    });

    return dates.map(d => ({
      date: d,
      slots: existingMap[d] && existingMap[d].length > 0
        ? existingMap[d]
        : [{ startTime: '10:00 AM', endTime: '06:00 PM' }]
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'startDate' || field === 'endDate') {
        const newStart = field === 'startDate' ? value : prev.startDate;
        const newEnd = field === 'endDate' ? value : prev.endDate;
        next.eventSchedules = recalculateDateSchedules(newStart, newEnd, prev.eventSchedules);
      }
      return next;
    });
  };

  const addTimeSlotToDate = (dateStr) => {
    setFormData(prev => ({
      ...prev,
      eventSchedules: (prev.eventSchedules || []).map(item => {
        if (item.date === dateStr) {
          return {
            ...item,
            slots: [...(item.slots || []), { startTime: '06:00 PM', endTime: '10:00 PM' }]
          };
        }
        return item;
      })
    }));
  };

  const updateTimeSlotInDate = (dateStr, slotIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      eventSchedules: (prev.eventSchedules || []).map(item => {
        if (item.date === dateStr) {
          const newSlots = [...(item.slots || [])];
          newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
          return { ...item, slots: newSlots };
        }
        return item;
      })
    }));
  };

  const removeTimeSlotFromDate = (dateStr, slotIndex) => {
    setFormData(prev => ({
      ...prev,
      eventSchedules: (prev.eventSchedules || []).map(item => {
        if (item.date === dateStr) {
          const newSlots = (item.slots || []).filter((_, idx) => idx !== slotIndex);
          return { ...item, slots: newSlots.length > 0 ? newSlots : [{ startTime: '10:00 AM', endTime: '06:00 PM' }] };
        }
        return item;
      })
    }));
  };

  const handleStateChange = (selectedState) => {
    setFormData(prev => ({
      ...prev,
      state: selectedState,
      city: '' // reset city when state changes
    }));
  };

  // Alphabetically Sorted States & Cities
  const availableStates = Object.keys(dbStatesMap).sort((a, b) => a.localeCompare(b));
  const availableCitiesForState = formData.state ? (dbStatesMap[formData.state] || []).sort((a, b) => a.localeCompare(b)) : [];

  const handleFileUpload = async (file, targetType) => {
    if (!file) return;

    const data = new FormData();
    data.append('media', file);

    if (targetType === 'poster') setUploadingPoster(true);
    if (targetType === 'banner') setUploadingBanner(true);

    try {
      const res = await instance.post('/media/partner/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedUrl = res?.url || res?.data?.url || res?.mediaUrl || res?.path;

      if (uploadedUrl) {
        if (targetType === 'poster') {
          handleChange('posterUrl', uploadedUrl);
        } else if (targetType === 'banner') {
          handleChange('bannerUrl', uploadedUrl);
        }
        alert('Image uploaded successfully!');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image.');
    } finally {
      if (targetType === 'poster') setUploadingPoster(false);
      if (targetType === 'banner') setUploadingBanner(false);
    }
  };

  const handleGalleryUpload = async (file) => {
    if (!file) return;

    if (formData.gallery.length >= 10) {
      alert('Maximum 10 gallery images allowed.');
      return;
    }

    setUploadingGallery(true);
    const data = new FormData();
    data.append('media', file);

    try {
      const res = await instance.post('/media/partner/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedUrl = res?.url || res?.data?.url || res?.mediaUrl || res?.path;

      if (uploadedUrl) {
        setFormData(prev => ({
          ...prev,
          gallery: [...prev.gallery, uploadedUrl]
        }));
        alert('Gallery photo uploaded!');
      }
    } catch (err) {
      console.error('Gallery Upload error:', err);
      alert('Failed to upload gallery photo.');
    } finally {
      setUploadingGallery(false);
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, idx) => idx !== index)
    }));
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      setDetectingLoc(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setFormData((prev) => ({
            ...prev,
            lat,
            lng,
            googleMapsUrl: `https://www.google.com/maps?q=${lat},${lng}`,
          }));
          setDetectingLoc(false);
        },
        (err) => {
          alert('Location detection failed. Please enter map link manually.');
          setDetectingLoc(false);
        }
      );
    }
  };

  const recalculateCapacity = (types) => {
    return types.reduce((sum, t) => sum + (Number(t.quantity) || 0), 0);
  };

  const addTicketType = () => {
    const newId = Date.now();
    setFormData((prev) => {
      const updatedTypes = [
        ...prev.ticketTypes,
        { id: newId, name: 'Student / Earlybird Pass', price: 299, quantity: 30, salesStart: '', salesEnd: '' },
      ];
      return {
        ...prev,
        ticketTypes: updatedTypes,
        maxCapacity: recalculateCapacity(updatedTypes),
      };
    });
  };

  const removeTicketType = (id) => {
    setFormData((prev) => {
      const updatedTypes = prev.ticketTypes.filter((t) => t.id !== id);
      return {
        ...prev,
        ticketTypes: updatedTypes,
        maxCapacity: recalculateCapacity(updatedTypes),
      };
    });
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      alert('Please enter event name.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        shortDescription: formData.shortDescription,
        description: formData.about || formData.description || formData.title,
        category: formData.category,
        eventFormat: formData.eventType === 'Online' ? 'ONLINE' : (formData.eventType === 'Hybrid' ? 'HYBRID' : 'IN_PERSON'),
        duration: formData.duration || '3 Hours',
        ageLimit: formData.ageLimit || '5yrs +',
        language: formData.language || 'Hindi',
        additionalInfo: {
          duration: formData.duration || '3 Hours',
          ageLimit: formData.ageLimit || '5yrs +',
          language: formData.language || 'Hindi',
        },
        startDate: formData.startDate || new Date().toISOString(),
        endDate: formData.endDate,
        eventSchedules: formData.eventSchedules || [],
        timezone: formData.timezone || 'Asia/Kolkata',
        venueName: formData.venueName,
        venueAddress: formData.venueAddress,
        areaLocality: formData.areaLocality,
        city: formData.city,
        state: formData.state,
        pinCode: formData.pinCode,
        country: formData.country || 'India',
        lat: formData.lat,
        lng: formData.lng,
        posterUrl: formData.posterUrl,
        bannerUrl: formData.bannerUrl,
        gallery: formData.gallery,
        capacity: formData.maxCapacity,
        maxPerUser: formData.registrationLimitPerUser,
        waitlistEnabled: formData.waitingListEnabled,
        ticketTypes: formData.ticketTypes,
        price: formData.ticketTypes?.[0]?.price || 0,
        submitForReview: true,
      };

      await EventService.createEvent(payload);
      alert('Event Submitted Successfully! It will appear live once approved by Admin.');
      history.push('/app/events');
    } catch (err) {
      console.error('Error creating event:', err);
      const errMsg = err.response?.data?.message || err.message || 'Failed to create event.';
      alert(`Error creating event: ${errMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: 'Basic Info & Media' },
    { num: 2, title: 'Date & Time' },
    { num: 3, title: 'Venue & Location' },
    { num: 4, title: 'Tickets' },
    { num: 5, title: 'Preview' },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6 text-left pb-32">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <button onClick={() => history.push('/app/events')} className="flex items-center gap-1 text-slate-600 hover:text-slate-900 text-xs font-bold mb-2 transition">
            <FiArrowLeft className="w-3.5 h-3.5" /> Back to Events
          </button>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
            <FiCalendar className="text-purple-700" /> Create Event
          </h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Configure event details, ticket tiers, schedules & venue</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => history.goBack()}
            className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all border border-slate-200"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Wizard Steps */}
      <div className="bg-white border border-slate-200 p-2 rounded-2xl flex items-center justify-between overflow-x-auto gap-2 shadow-sm">
        {steps.map((s) => (
          <button
            key={s.num}
            onClick={() => setStep(s.num)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all shrink-0 ${
              step === s.num
                ? 'bg-purple-700 text-white shadow-xs'
                : step > s.num
                ? 'bg-purple-50 text-purple-900 border border-purple-200'
                : 'bg-slate-50 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
              step === s.num ? 'bg-white text-purple-700 font-extrabold' : 'bg-slate-200 text-slate-700'
            }`}>
              {s.num}
            </span>
            <span>{s.title}</span>
          </button>
        ))}
      </div>

      {/* STEP 1: BASIC INFO & MEDIA UPLOAD */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-6 shadow-sm">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">Event Format *</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { type: 'Online', desc: 'Virtual stream or link', icon: FiGlobe },
                  { type: 'IRL', desc: 'Physical venue attendance', icon: FiMapPin, popular: true },
                  { type: 'Hybrid', desc: 'Both Physical + Stream', icon: FiUsers },
                ].map((t) => {
                  const Icon = t.icon;
                  const isSel = formData.eventType === t.type;
                  return (
                    <div
                      key={t.type}
                      onClick={() => handleChange('eventType', t.type)}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        isSel
                          ? 'bg-purple-50 border-purple-600 shadow-xs'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <Icon className={`w-6 h-6 ${isSel ? 'text-purple-700' : 'text-slate-400'}`} />
                        {t.popular && (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-[9px] font-bold">
                            RECOMMENDED
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 mt-3">{t.type} {t.type === 'IRL' ? '/ Offline' : ''}</h3>
                      <p className="text-[11px] text-slate-500 font-medium mt-0.5">{t.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Event Name *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g. Punjab Grand Cultural & Food Fest 2026"
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Select Category *</label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
              >
                <option value="">Select Category</option>
                {categories.map((c, idx) => {
                  const name = getCategoryName(c);
                  const catId = c._id || name;
                  return <option key={c._id || idx} value={catId}>{name}</option>;
                })}
              </select>
            </div>

            {/* State & City Selector */}
            <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-4">
              <h3 className="text-xs font-bold text-emerald-800 flex items-center gap-1.5 uppercase tracking-wider">
                <FiMapPin className="w-4 h-4 text-emerald-600" /> State & Searchable City Selector
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">1. Select State *</label>
                  <SearchableSelect
                    options={availableStates}
                    value={formData.state}
                    onChange={handleStateChange}
                    placeholder="Search & Select State..."
                    searchPlaceholder="Type state name..."
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">2. Select City *</label>
                  <SearchableSelect
                    options={availableCitiesForState}
                    value={formData.city}
                    onChange={(selectedCity) => handleChange('city', selectedCity)}
                    placeholder={formData.state ? `Search city in ${formData.state}...` : 'First select a State'}
                    searchPlaceholder={`Type city in ${formData.state || 'state'}...`}
                    disabled={!formData.state}
                  />
                </div>
              </div>
            </div>

            {/* Poster & Banner Upload */}
            <div className="pt-4 border-t border-slate-200 space-y-4">
              <h3 className="text-xs font-bold text-purple-700 flex items-center gap-1.5 uppercase tracking-wider">
                <FiImage className="w-4 h-4" /> Poster & Banner Media
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                  <label className="block text-xs font-bold text-slate-700">Poster Card Image URL</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={formData.posterUrl}
                      onChange={(e) => handleChange('posterUrl', e.target.value)}
                      placeholder="https://..."
                      className="flex-1 bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                    />
                    <label className="px-3 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1 shrink-0 shadow-sm">
                      <FiUploadCloud />
                      <span>{uploadingPoster ? 'Uploading...' : 'Upload'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingPoster}
                        onChange={(e) => handleFileUpload(e.target.files[0], 'poster')}
                      />
                    </label>
                  </div>
                  {formData.posterUrl && (
                    <div className="w-20 h-24 rounded-lg overflow-hidden border border-purple-200">
                      <img src={formData.posterUrl} alt="Poster preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                  <label className="block text-xs font-bold text-slate-700">Banner Cover Image URL</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={formData.bannerUrl}
                      onChange={(e) => handleChange('bannerUrl', e.target.value)}
                      placeholder="https://..."
                      className="flex-1 bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                    />
                    <label className="px-3 py-2.5 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1 shrink-0 shadow-sm">
                      <FiUploadCloud />
                      <span>{uploadingBanner ? 'Uploading...' : 'Upload'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingBanner}
                        onChange={(e) => handleFileUpload(e.target.files[0], 'banner')}
                      />
                    </label>
                  </div>
                  {formData.bannerUrl && (
                    <div className="w-32 h-18 rounded-lg overflow-hidden border border-indigo-200">
                      <img src={formData.bannerUrl} alt="Banner preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Gallery Images */}
            <div className="pt-4 border-t border-slate-200 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold text-amber-700 flex items-center gap-1.5 uppercase tracking-wider">
                    <FiImage className="w-4 h-4" /> Gallery Photos (Max 10)
                  </h3>
                  <p className="text-[10px] text-slate-500 font-medium mt-0.5">Photos displayed in event gallery showcase</p>
                </div>

                {formData.gallery.length < 10 && (
                  <label className="px-3.5 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1.5 shadow-sm">
                    <FiUploadCloud />
                    <span>{uploadingGallery ? 'Uploading...' : '+ Upload Gallery Photo'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingGallery}
                      onChange={(e) => handleGalleryUpload(e.target.files[0])}
                    />
                  </label>
                )}
              </div>

              {formData.gallery.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {formData.gallery.map((imgUrl, idx) => (
                    <div key={idx} className="relative h-24 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 group">
                      <img src={imgUrl} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(idx)}
                        className="absolute top-1.5 right-1.5 p-1 rounded-lg bg-red-600/90 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove photo"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center rounded-xl bg-slate-50 border border-dashed border-slate-300 text-slate-500 font-medium text-xs">
                  No gallery photos added yet.
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Detailed Description</label>
              <textarea
                rows={4}
                value={formData.about}
                onChange={(e) => handleChange('about', e.target.value)}
                placeholder="Explain what attendees can expect, schedule highlights..."
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
              />
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: DATE & TIME */}
      {step === 2 && (
        <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Start Date *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">End Date *</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleChange('endDate', e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>
          </div>

          {/* Generated Event Dates & Time Slots UI */}
          <div className="p-4 rounded-xl bg-purple-50 border border-purple-200 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs font-bold text-purple-900 uppercase tracking-wider flex items-center gap-1.5">
                  <FiCalendar className="w-4 h-4 text-purple-700" /> Auto-Generated Event Dates & Daily Time Slots
                </h3>
                <p className="text-[11px] text-slate-600 font-medium mt-0.5">
                  Selecting Start & End dates automatically creates all intermediate dates. You can set multiple time slots per date!
                </p>
              </div>
            </div>

            {formData.eventSchedules && formData.eventSchedules.length > 0 ? (
              <div className="space-y-3">
                {formData.eventSchedules.map((sch, dateIdx) => {
                  const dObj = new Date(sch.date);
                  const formattedDate = !isNaN(dObj.getTime())
                    ? dObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                    : sch.date;

                  return (
                    <div key={sch.date || dateIdx} className="p-4 rounded-xl bg-white border border-slate-200 space-y-3 shadow-xs">
                      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                        <span className="text-xs font-black text-slate-900 flex items-center gap-2">
                          📅 <span className="text-purple-700">{formattedDate}</span>
                          <span className="text-[10px] text-slate-500 font-mono font-normal">({sch.date})</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => addTimeSlotToDate(sch.date)}
                          className="px-2.5 py-1 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-900 text-[11px] font-bold transition-all flex items-center gap-1 border border-purple-200"
                        >
                          <FiPlus className="w-3 h-3 text-purple-700" /> Add Time Slot
                        </button>
                      </div>

                      {/* Time Slots */}
                      <div className="space-y-2">
                        {(sch.slots || []).map((slot, slotIdx) => (
                          <div key={slotIdx} className="flex items-center gap-3 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                            <span className="text-[11px] font-bold text-slate-600 shrink-0">Slot #{slotIdx + 1}</span>

                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[9px] font-bold text-slate-600 uppercase mb-0.5">Start Time</label>
                                <input
                                  type="text"
                                  value={slot.startTime}
                                  onChange={(e) => updateTimeSlotInDate(sch.date, slotIdx, 'startTime', e.target.value)}
                                  placeholder="e.g. 10:00 AM"
                                  className="w-full bg-white border border-slate-300 rounded-lg p-1.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
                                />
                              </div>
                              <div>
                                <label className="block text-[9px] font-bold text-slate-600 uppercase mb-0.5">End Time</label>
                                <input
                                  type="text"
                                  value={slot.endTime}
                                  onChange={(e) => updateTimeSlotInDate(sch.date, slotIdx, 'endTime', e.target.value)}
                                  placeholder="e.g. 06:00 PM"
                                  className="w-full bg-white border border-slate-300 rounded-lg p-1.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
                                />
                              </div>
                            </div>

                            {sch.slots.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeTimeSlotFromDate(sch.date, slotIdx)}
                                className="p-1.5 text-red-600 hover:text-red-700 rounded-lg hover:bg-red-50 shrink-0"
                                title="Remove time slot"
                              >
                                <FiTrash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center rounded-xl bg-white border border-dashed border-slate-300 text-slate-500 font-medium text-xs">
                Select a Start Date above to view automatically generated dates and daily time slots.
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">⏳ Duration (e.g. 5 Hours)</label>
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => handleChange('duration', e.target.value)}
                placeholder="e.g. 5 Hours or 3.5 Hours"
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">👥 Age Limit (e.g. 8yrs +)</label>
              <input
                type="text"
                value={formData.ageLimit}
                onChange={(e) => handleChange('ageLimit', e.target.value)}
                placeholder="e.g. 8yrs + or All Ages"
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">🗣️ Language (e.g. Hindi)</label>
              <input
                type="text"
                value={formData.language}
                onChange={(e) => handleChange('language', e.target.value)}
                placeholder="e.g. Hindi, English"
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: VENUE & LOCATION */}
      {step === 3 && (
        <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-sm">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">Venue Name</label>
            <input
              type="text"
              value={formData.venueName}
              onChange={(e) => handleChange('venueName', e.target.value)}
              placeholder="e.g. Royal Palace Auditorium"
              className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Selected State</label>
              <input
                type="text"
                readOnly
                value={formData.state || 'Not selected yet'}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600 outline-none font-semibold"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Selected City</label>
              <input
                type="text"
                readOnly
                value={formData.city || 'Not selected yet'}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600 outline-none font-semibold"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="text"
              value={formData.googleMapsUrl}
              onChange={(e) => handleChange('googleMapsUrl', e.target.value)}
              placeholder="Google Maps URL"
              className="flex-1 bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
            />
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              className="px-4 py-3 rounded-xl bg-purple-50 text-purple-900 border border-purple-200 text-xs font-bold hover:bg-purple-100 transition-all shrink-0 shadow-xs"
            >
              {detectingLoc ? 'Detecting...' : 'Use Current GPS'}
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: TICKETS */}
      {step === 4 && (
        <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-6 shadow-sm">
          {/* Event Capacity Setting Card */}
          <div className="p-4 rounded-xl bg-purple-50 border border-purple-200 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider flex items-center gap-1.5">
                <FiUsers className="text-purple-700" /> Event Capacity / Total Seats *
              </h4>
              <p className="text-[11px] text-slate-600 font-medium mt-0.5">Set maximum number of attendees/tickets allowed for this event</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-700">Total Seats:</span>
              <input
                type="number"
                value={formData.maxCapacity}
                onChange={(e) => handleChange('maxCapacity', Number(e.target.value))}
                placeholder="e.g. 500"
                className="w-32 bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 font-bold outline-none focus:border-purple-600 text-center"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Ticket Tiers</h3>
              <p className="text-[11px] text-slate-500 font-medium">Configure ticket passes, pricing, and available quantities per tier</p>
            </div>
            <button
              type="button"
              onClick={addTicketType}
              className="px-3 py-1.5 rounded-lg bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition-colors flex items-center gap-1 shadow-sm"
            >
              <FiPlus className="w-3.5 h-3.5" /> Add Tier
            </button>
          </div>

          <div className="space-y-3">
            {formData.ticketTypes.map((t) => (
              <div key={t.id} className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="w-full md:w-1/3">
                  <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Pass Name</label>
                  <input
                    type="text"
                    value={t.name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        ticketTypes: prev.ticketTypes.map(x => x.id === t.id ? { ...x, name: newName } : x)
                      }));
                    }}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-900 font-bold outline-none focus:border-purple-600"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Price (₹)</label>
                    <input
                      type="number"
                      value={t.price}
                      onChange={(e) => {
                        const newPrice = Number(e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          ticketTypes: prev.ticketTypes.map(x => x.id === t.id ? { ...x, price: newPrice } : x)
                        }));
                      }}
                      className="w-24 bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-900 font-bold outline-none focus:border-purple-600"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Qty / Seats</label>
                    <input
                      type="number"
                      value={t.quantity !== undefined ? t.quantity : 100}
                      onChange={(e) => {
                        const newQty = Number(e.target.value);
                        setFormData(prev => {
                          const updatedTypes = prev.ticketTypes.map(x => x.id === t.id ? { ...x, quantity: newQty } : x);
                          return {
                            ...prev,
                            ticketTypes: updatedTypes,
                            maxCapacity: recalculateCapacity(updatedTypes),
                          };
                        });
                      }}
                      className="w-24 bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-900 font-bold outline-none focus:border-purple-600"
                    />
                  </div>
                </div>

                <button type="button" onClick={() => removeTicketType(t.id)} className="text-red-600 hover:text-red-700 p-1 self-end md:self-center">
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STEP 5: PREVIEW */}
      {step === 5 && (
        <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-sm">
          <h3 className="text-base font-black text-slate-900">{formData.title || 'Untitled Event'}</h3>
          <p className="text-xs text-slate-600 font-medium">{formData.about}</p>
          <div className="flex items-center gap-4 text-xs text-purple-700 font-bold">
            <span>Type: {formData.eventType}</span>
            <span>Category: {formData.category}</span>
            <span>State: {formData.state || 'TBD'}</span>
            <span>City: {formData.city || 'TBD'}</span>
            <span>Gallery Photos: {formData.gallery.length}</span>
          </div>
        </div>
      )}

      {/* Wizard Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 border-t border-slate-200 backdrop-blur-md z-30 flex items-center justify-between max-w-6xl mx-auto rounded-t-2xl shadow-lg">
        <button
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-bold disabled:opacity-50 flex items-center gap-2"
        >
          <FiArrowLeft /> Back
        </button>

        {step < 5 ? (
          <button
            onClick={() => setStep((s) => Math.min(5, s + 1))}
            className="px-6 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold flex items-center gap-2 shadow-sm"
          >
            <span>Continue</span> <FiArrowRight />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-8 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-2 shadow-sm"
          >
            <FiCheck className="w-4 h-4" /> <span>{loading ? 'Publishing...' : 'Publish Event Live'}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateEvent;
